/**
 * Mark-to-market for virtual paper positions using live scanner rows (Mid/Last).
 */
import type { ScannerData } from '../types/ScannerData';
import type { StrategyLeg, TradingCall } from '../types/TradingCall';

function rowMark(r: ScannerData): number {
  const m = r.Mid;
  if (m != null && Number.isFinite(m) && m > 0) return m;
  const l = r.Last;
  if (l != null && Number.isFinite(l) && l > 0) return l;
  const t = r['Theoretical Value'];
  if (t != null && Number.isFinite(t) && t > 0) return t;
  return 0;
}

function normExp(s: string | undefined): string {
  return (s || '').trim().split('T')[0];
}

function rowsForUnderlying(rows: ScannerData[], underlying: string): ScannerData[] {
  const u = underlying.trim();
  return rows.filter((r) => (r.Underlying || r.underlying_symbol || '').trim() === u);
}

function findStrikeRow(
  subset: ScannerData[],
  strike: number,
  right: 'C' | 'P',
  expiry: string
): ScannerData | undefined {
  const e = normExp(expiry);
  return subset.find(
    (r) =>
      r['Option Type'] === right &&
      Math.abs(Number(r.Strike) - strike) < 0.02 &&
      normExp(r['Expiry Date']) === e
  );
}

/** Mark multi-leg position using stored legs (same strikes/expiry as open). */
function markFromLegs(legs: StrategyLeg[] | undefined, subset: ScannerData[]): number | null {
  if (!legs?.length) return null;
  let sum = 0;
  for (const leg of legs) {
    const right: 'C' | 'P' = leg.optionType === 'CALL' ? 'C' : 'P';
    const row = findStrikeRow(subset, leg.strike, right, leg.expiry);
    if (!row) return null;
    const m = rowMark(row);
    const q = leg.quantity && leg.quantity > 0 ? leg.quantity : 1;
    if (leg.type === 'BUY') sum += m * q;
    else sum -= m * q;
  }
  if (!Number.isFinite(sum)) return null;
  return sum;
}

function atmPair(
  subset: ScannerData[],
  underlyingPx: number
): { call?: ScannerData; put?: ScannerData } {
  const calls = subset.filter((r) => r['Option Type'] === 'C');
  const puts = subset.filter((r) => r['Option Type'] === 'P');
  if (!calls.length || !puts.length) return {};
  const call = [...calls].sort((a, b) => Math.abs(a.Strike - underlyingPx) - Math.abs(b.Strike - underlyingPx))[0];
  const put = [...puts].sort((a, b) => Math.abs(a.Strike - underlyingPx) - Math.abs(b.Strike - underlyingPx))[0];
  return { call, put };
}

function otmStranglePair(subset: ScannerData[], underlyingPx: number): { call?: ScannerData; put?: ScannerData } {
  const otmCall = subset
    .filter((r) => r['Option Type'] === 'C' && r.Strike > underlyingPx)
    .sort((a, b) => a.Strike - b.Strike)[0];
  const otmPut = subset
    .filter((r) => r['Option Type'] === 'P' && r.Strike < underlyingPx)
    .sort((a, b) => b.Strike - a.Strike)[0];
  return { call: otmCall, put: otmPut };
}

/**
 * Best-effort mark for one virtual position. Returns null if no match in current rows.
 */
export function estimateMarkPrice(call: TradingCall, rows: ScannerData[]): number | null {
  if (!rows?.length) return null;
  const subset = rowsForUnderlying(rows, call.underlying);
  if (!subset.length) return null;

  const up = subset[0]?.['Underlying Price'] || 0;
  const t = call.strategy.type;
  const d = call.strategy.details;

  if (t === 'STRADDLE') {
    const fromLegs = markFromLegs(d.legs, subset);
    if (fromLegs != null && fromLegs > 0) return fromLegs;
    const { call: c, put: p } = atmPair(subset, up || Number(d.strike) || 100);
    if (!c || !p) return null;
    const m = rowMark(c) + rowMark(p);
    return m > 0 ? m : null;
  }

  if (t === 'STRANGLE') {
    const fromLegs = markFromLegs(d.legs, subset);
    if (fromLegs != null && fromLegs > 0) return fromLegs;
    const { call: c, put: p } = otmStranglePair(subset, up || 100);
    if (!c || !p) return null;
    const m = rowMark(c) + rowMark(p);
    return m > 0 ? m : null;
  }

  if (t === 'NAKED_CALL' || t === 'NAKED_PUT') {
    const right: 'C' | 'P' = t === 'NAKED_CALL' ? 'C' : 'P';
    const strike = Number(d.strike);
    const exp = String(d.expiry || '');
    const row = findStrikeRow(subset, strike, right, exp);
    if (!row) return null;
    const m = rowMark(row);
    return m > 0 ? m : null;
  }

  if (t === 'COLLAR') {
    const fromLegs = markFromLegs(d.legs, subset);
    if (fromLegs != null && Number.isFinite(fromLegs) && fromLegs !== 0) {
      return Math.abs(fromLegs);
    }
    const { call: c, put: p } = atmPair(subset, up || 100);
    if (c && p) {
      const m = rowMark(p) - rowMark(c);
      return Number.isFinite(m) && m !== 0 ? Math.abs(m) : null;
    }
    return null;
  }

  if (t === 'VERTICAL_SPREAD') {
    const fromLegs = markFromLegs(d.legs, subset);
    if (fromLegs != null && Number.isFinite(fromLegs)) {
      return Math.abs(fromLegs);
    }
    const bull = call.direction === 'BUY';
    const calls = subset.filter((r) => r['Option Type'] === 'C');
    const puts = subset.filter((r) => r['Option Type'] === 'P');
    if (bull && calls.length >= 2) {
      const sorted = [...calls].sort((a, b) => b.Strike - a.Strike);
      const longLeg = sorted[sorted.length - 1];
      const shortLeg = sorted[0];
      const m = rowMark(longLeg) - rowMark(shortLeg);
      return Number.isFinite(m) ? Math.abs(m) : null;
    }
    if (!bull && puts.length >= 2) {
      const sorted = [...puts].sort((a, b) => a.Strike - b.Strike);
      const longLeg = sorted[sorted.length - 1];
      const shortLeg = sorted[0];
      const m = rowMark(longLeg) - rowMark(shortLeg);
      return Number.isFinite(m) ? Math.abs(m) : null;
    }
  }

  return null;
}
